/**
 * Steps to add a new notification type:
 * Notifications have several parts that need to be added concurrently in order to function
 * correctly. The lists are in alphabetical order to make navigation easier, so try to add
 * new items in the same way.
 * 1. translation keys
 *      There should be three keys for each notification: type.head, type.body, type.link. They
 *      need to be added to the localization worksheet
 * 2. route
 *      The route for the link should be added to getNotificationRoute() in NotificationRoutes.tsx.
 *      When no route is implemented yet, the default should return {route: "Route not yet implemented"}
 * 3. display strings
 *      If there are variables used in the display string, they need to be included in
 *      formatDisplayStrings() in this file. Most of the common ones are already there, but
 *      you should double check.
 * 4. icon
 *      - The notification needs an icon. Get the correct icon from the design team or
 *        src/static/images/notifications and rename it to correspond to the appropriate
 *        notification type and css class (i.e. replace "." with "_").
 *      - Update .notification_icon in Notification.scss with an appropriate css class
 *        that corresponds to the icon you just created and set the background image to the new icon
 * 5. NotificationTypeMap
 *      Add the notificationType and the css class to NotificationIconMap in
 *      NotificationIcons.tsx. This will remove the default notification message
 *      and the notification should be working correctly now.
 */
import { navigateAddModal, navigateRemoveModal, setNavigationPath } from 'client/actions/navigation'
import { updateIsDeleted, updateIsRead } from 'client/api/notifications'
import { getLocation } from 'client/globalWindow'
import { NotificationInterface } from 'client/Interfaces/notifications/NotificationsInterface'
import { l } from 'client/Other/Translation'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import { Analytics, checkPlacement } from '../../Global/Analytics'
import {
  AnalyticsObject,
  AnalyticsType,
  Category,
  deliveryType,
  Events,
  Placement,
  PremiumModalOrigin,
  Result,
  trackingFields,
} from '../../Global/AnalyticsEvents'
import useFormats from '../DocumentsV1/hooks/useFormats'
import './Notification.scss'
import { NotificationIconMap } from './NotificationIcons'
import { getNotificationRoute } from './NotificationRoutes'

interface INotificationProps {
  notification: NotificationInterface
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  innerRef?: any
}

export const Notification: React.FunctionComponent<INotificationProps> = (props) => {
  const type = props.notification.type as string
  const { formatDateString, getCurrencyFormattedValue } = useFormats()
  const date = formatDateString(props.notification.createdAt)
  const [isHidden, setIsHidden] = useState(false)
  const [route, setRoute] = useState<string | undefined>()
  const [modal, setModal] = useState<string | undefined>()
  const dispatch = useDispatch()

  useEffect(() => {
    const routeObj = getNotificationRoute(props.notification)
    setRoute(routeObj.route)
    setModal(routeObj.modal)
  }, [])

  // Replaces placeholders in translation strings
  const formatDisplayStrings = (displayString: string, notification: NotificationInterface) => {
    if (!displayString) {
      return ''
    }
    const planString = displayString.replaceAll('{{plan_name}}', notification.data?.planName as string)
    const customerString = planString.replaceAll('{{customer_name}}', notification.data?.customerName as string)
    const emailString = customerString.replaceAll('{{customer_email}}', notification.data?.email as string)
    const invoiceNumberString = emailString.replaceAll('{{invoice_number}}', notification.data?.invoiceNumber as string)
    const estimateNumberString = invoiceNumberString.replaceAll(
      '{{estimate_number}}',
      notification.data?.estimateNumber as string,
    )
    const creditNoteString = estimateNumberString.replaceAll(
      '{{creditnote_number}}',
      notification.data?.creditNoteNumber as string,
    )
    const receiptNumberString = creditNoteString.replaceAll(
      '{{receipt_number}}',
      notification.data?.receiptNumber as string,
    )
    const totalString = receiptNumberString.replaceAll(
      '{{invoice_total_amount}}',
      getCurrencyFormattedValue(+notification.data?.totalAmount, { currency: notification.data?.currency }),
    )
    const currencyString = totalString.replaceAll('{{currency}}', '')
    const paidDateString = currencyString.replaceAll(
      '{{invoice_paid_date}}',
      formatDateString(notification.data?.paidDate),
    )

    return paidDateString
  }

  const notificationUpdateIsRead = async () => {
    const updatedNotifications: { notifications: [NotificationInterface] } = {
      notifications: [{ ...props.notification, isRead: true }],
    }

    const updateRead = await updateIsRead(updatedNotifications)
  }

  const notificationUpdateIsDeleted = async (newValue: boolean) => {
    const updatedNotifications: { notifications: [NotificationInterface] } = {
      notifications: [{ ...props.notification, isDeleted: newValue }],
    }
    const updateDelete = await updateIsDeleted(updatedNotifications, newValue)

    if (newValue && !props.notification.isRead) {
      notificationUpdateIsRead()
    }

    setIsHidden(true)
  }

  const openModal = React.useCallback(
    (error?: string) => {
      dispatch(
        navigateAddModal({
          component: modal,
          props: {
            redirectUrl: route,
            isOpen: true,
            connectionError: error,
            onClose: () => {
              dispatch(navigateRemoveModal(modal))
              dispatch(setNavigationPath(route))
            },
          },
        }),
      )
    },
    [dispatch, modal, route],
  )

  const handleLinkClick = () => {
    notificationUpdateIsRead()
    if (route === 'Route not yet implemented') {
      return
    }
    if (!modal) {
      dispatch(setNavigationPath(route))
    } else {
      dispatch(setNavigationPath(route))
      openModal()
    }
    props.setOpen(false)
  }

  return (
    <>
      {type in NotificationIconMap ? (
        <div
          className={'notification' + (isHidden ? ' hidden' : '')}
          ref={props.innerRef}
          data-automation="notification"
        >
          <div className="notification__isRead">
            {!props.notification.isRead && <div className="notification__isRead-ellipse"></div>}
          </div>
          <div className="notification__icon">
            <div className={'notification__icon-img ' + NotificationIconMap[type]}></div>
          </div>
          <div className="notification__info">
            <div className="notification__info-header" data-automation="notification-info-header">
              {formatDisplayStrings(l(type + '.head') as string, props.notification)}
            </div>
            <div className="notification__info-content" data-automation="notification-info-content">
              {formatDisplayStrings(l(type + '.body') as string, props.notification)}
            </div>
            <div className="notification__info-link-wrapper">
              {/* This would usually be an <a> tag, but React doesn't allow an <a> tag
                                to be used as the child of another <a>. */}
              <div
                className="notification__info-link"
                data-automation="notification-info-link"
                onClick={() => {
                  handleLinkClick()
                }}
              >
                {l(type + '.link')}
              </div>
            </div>
          </div>
          <div className="notification__right">
            <div className="notification__right-date" data-automation="notification-date">
              {date}
            </div>
            {props.notification.isDeleted ? (
              <div
                className="notification__right-link notification__right-markRead tooltip"
                onClick={() => notificationUpdateIsDeleted(false)}
              >
                <span className="notification__right-markRead-restore"></span>
                <div className="tooltip-container">{l('notifications.modal.tooltip.restore')}</div>
              </div>
            ) : (
              <div
                className="notification__right-link notification__right-markRead tooltip"
                onClick={() => notificationUpdateIsDeleted(true)}
              >
                <span className="notification__right-markRead-delete" data-automation="notification-mark-read"></span>
                <div className="tooltip-container">{l('notifications.modal.tooltip.delete')}</div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {getLocation().host.includes('zerv.me') && (
            <div className={'notification' + (isHidden ? ' hidden' : '')} ref={props.innerRef}>
              <div className="notification__info">
                <div className="notification__info-header">Unhandled Notification Type: {type}</div>
                <div className="notification__info-content">
                  This notification type has not been added to the NotificationIconMap
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  )
}
